import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tag, MapPin, Send, Mail, FileSearch } from "lucide-react";
import { api, photoUrl, formatApiError } from "../api";
import { QUOTE_STATUS, fmtDate, fmtDistance, fmtKm, fmtTry } from "../lib/labels";
import VehicleDossierModal from "./VehicleDossierModal";
import { QuoteLifecycleNote, QuoteCountdown } from "./QuoteLifecycle";

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 text-sm focus:outline-none focus:border-cyan-400/60 transition-colors";
const labelCls = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5";
const EMPTY_OFFER = { price: "", description: "", eta: "", brand_product: "", warranty: "", valid_days: "" };

export default function QuoteInbox() {
  const [data, setData] = useState(null);
  const [openForm, setOpenForm] = useState(null);
  const [offer, setOffer] = useState({ ...EMPTY_OFFER });
  const [sending, setSending] = useState(false);
  const [dossierFor, setDossierFor] = useState(null);

  const load = () => api.get("/teklif/isletme").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);

  const startOffer = (r) => {
    if (openForm === r.id) { setOpenForm(null); return; }
    setOffer(r.my_offer ? {
      price: r.my_offer.price ?? "", description: r.my_offer.description || "", eta: r.my_offer.eta || "",
      brand_product: r.my_offer.brand_product || "", warranty: r.my_offer.warranty || "", valid_days: "",
    } : { ...EMPTY_OFFER });
    setOpenForm(r.id);
  };

  const submitOffer = async (tid) => {
    setSending(true);
    try {
      await api.post(`/teklif/talep/${tid}/teklif`, {
        price: offer.price === "" ? null : Number(offer.price),
        description: offer.description, eta: offer.eta, brand_product: offer.brand_product, warranty: offer.warranty,
        valid_days: offer.valid_days === "" ? null : Number(offer.valid_days),
      });
      toast.success("Teklifiniz araç sahibine iletildi");
      setOpenForm(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSending(false);
  };

  return (
    <div data-testid="quote-inbox" className="rounded-2xl border border-emerald-500/20 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7">
      <h2 className="text-lg font-semibold text-slate-100 mb-1 flex items-center gap-2">
        <Tag className="w-5 h-5 text-emerald-300" /> Teklif Talepleri ({data ? data.items.length : 0})
      </h2>
      <p className="text-xs text-slate-500 mb-4">Kategorinize ve ilçenize uyan araç sahibi talepleri.</p>
      <div className="mb-4"><QuoteLifecycleNote testid="quote-inbox-lifecycle-note" /></div>
      {data === null ? (
        <div className="flex justify-center py-6"><div className="w-7 h-7 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>
      ) : data.items.length === 0 ? (
        <p className="text-sm text-slate-500">Şu an size uyan teklif talebi yok.</p>
      ) : (
        <div className="space-y-3">
          {data.items.map((r) => {
            const st = QUOTE_STATUS[r.status] || QUOTE_STATUS.bekliyor;
            const closed = ["kabul_edildi"].includes(r.status) && !(r.my_offer && r.my_offer.status === "kabul");
            return (
              <div key={r.id} data-testid={`quote-inbox-${r.id}`} className="rounded-xl border border-white/10 bg-slate-950/50 px-4 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-slate-100">{r.category_label} — {[r.brand, r.model].filter(Boolean).join(" ") || "Araç"}{r.plate && ` (${r.plate})`}</p>
                    {r.tow_scope && (
                      <span data-testid={`tow-scope-badge-${r.id}`} className="inline-block mt-1 text-[10px] px-2.5 py-1 rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-300 font-bold">
                        {r.tow_scope === "sehirler_arasi" ? "ŞEHİRLER ARASI ÇEKİCİ" : "ŞEHİR İÇİ ÇEKİCİ"}
                      </span>
                    )}
                    {r.tow_scope === "sehirler_arasi" && r.from_city && (
                      <p data-testid={`tow-route-${r.id}`} className="text-xs text-cyan-300 mt-1">Güzergâh: {r.from_city} / {r.from_district} → {r.to_city} / {r.to_district}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">
                      <MapPin className="w-3.5 h-3.5" /> {[r.city, r.district].filter(Boolean).join(" / ")} • {fmtDate(r.created_at)}
                      {r.year && ` • ${r.year}`}{r.km != null && ` • ${fmtKm(r.km)}`}
                    </p>
                    {r.mesafe_km != null && <p className="text-xs text-cyan-300 mt-1 font-semibold">{fmtDistance(r.mesafe_km)}</p>}
                    {r.part_name && <p className="text-xs text-cyan-300 mt-1">İstenen parça: {r.part_name}</p>}
                    {r.expected_price != null && <p className="text-xs text-cyan-300 mt-1">Fiyat beklentisi: {fmtTry(r.expected_price)}</p>}
                    {r.description && <p className="text-xs text-slate-400 mt-1">{r.description}</p>}
                    {r.photos && r.photos.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {r.photos.map((p) => <img key={p} src={photoUrl(p)} alt="" className="w-14 h-10 object-cover rounded-lg border border-white/10" />)}
                      </div>
                    )}
                    <p className="text-xs text-slate-500 mt-1.5">Talep eden: {r.user_name || "Araç sahibi"}</p>
                    {r.user_email && (
                      <p data-testid={`quote-owner-email-${r.id}`} className="text-xs text-emerald-300 mt-1 flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" /> {r.user_email}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${st.cls}`}>{st.label}</span>
                    <QuoteCountdown createdAt={r.created_at} testid={`quote-inbox-countdown-${r.id}`} />
                    {r.vin && (
                      <button data-testid={`quote-dossier-btn-${r.id}`} onClick={() => setDossierFor(r)}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-400/10 border border-cyan-400/40 text-cyan-300 text-xs font-bold hover:bg-cyan-400/20 transition-colors">
                        <FileSearch className="w-3.5 h-3.5" /> Araç Dosyası
                      </button>
                    )}
                    {!closed && r.offer_open !== false && (
                      <button data-testid={`quote-offer-btn-${r.id}`} onClick={() => startOffer(r)}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-bold hover:bg-emerald-500/20 transition-colors">
                        <Send className="w-3.5 h-3.5" /> {r.my_offer ? "Teklifimi Düzenle" : "Teklif Ver"}
                      </button>
                    )}
                    {!closed && r.offer_open === false && (
                      <span data-testid={`quote-offer-closed-${r.id}`} className="text-[11px] px-3 py-1.5 rounded-full border border-red-500/30 text-red-300 font-semibold">
                        Yeni teklif alınmıyor
                      </span>
                    )}
                  </div>
                </div>

                {r.my_offer && openForm !== r.id && (
                  <div className="flex items-center gap-2 flex-wrap mt-2 border-t border-white/5 pt-2">
                    <p data-testid={`quote-my-offer-${r.id}`} className="text-xs text-emerald-300/90">
                      Teklifiniz: {r.my_offer.price != null ? fmtTry(r.my_offer.price) : "-"}{r.my_offer.eta && ` • ${r.my_offer.eta}`}{r.my_offer.valid_until && ` • Geçerlilik: ${fmtDate((r.my_offer.valid_until || "").slice(0, 10))}`}
                    </p>
                    <span data-testid={`quote-my-offer-status-${r.id}`} className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${
                      r.my_offer.status === "kabul" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                      : r.my_offer.status === "reddedildi" ? "border-white/10 text-slate-500"
                      : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
                      {r.my_offer.status === "kabul" ? "Kabul Edildi" : r.my_offer.status === "reddedildi" ? "Seçilmedi" : "Yanıt Bekliyor"}
                    </span>
                  </div>
                )}

                {openForm === r.id && (
                  <div data-testid={`quote-offer-form-${r.id}`} className="mt-3 border-t border-white/5 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><label className={labelCls}>Fiyat (₺)</label>
                      <input data-testid={`quote-offer-price-${r.id}`} type="number" min="0" value={offer.price}
                        onChange={(e) => setOffer({ ...offer, price: e.target.value })} className={inputCls} placeholder="örn. 4500" /></div>
                    <div><label className={labelCls}>Tahmini Süre</label>
                      <input data-testid={`quote-offer-eta-${r.id}`} value={offer.eta}
                        onChange={(e) => setOffer({ ...offer, eta: e.target.value })} className={inputCls} placeholder="örn. 2 saat / 1 gün / 30 dk içinde oradayım" /></div>
                    <div><label className={labelCls}>Kullanılan Ürün / Marka</label>
                      <input data-testid={`quote-offer-brand-${r.id}`} value={offer.brand_product}
                        onChange={(e) => setOffer({ ...offer, brand_product: e.target.value })} className={inputCls} placeholder="örn. 3M cam filmi" /></div>
                    <div><label className={labelCls}>Garanti</label>
                      <input data-testid={`quote-offer-warranty-${r.id}`} value={offer.warranty}
                        onChange={(e) => setOffer({ ...offer, warranty: e.target.value })} className={inputCls} placeholder="örn. 2 yıl garanti" /></div>
                    <div><label className={labelCls}>Teklif Geçerlilik Süresi (gün)</label>
                      <input data-testid={`quote-offer-valid-${r.id}`} type="number" min="1" max="90" value={offer.valid_days}
                        onChange={(e) => setOffer({ ...offer, valid_days: e.target.value })} className={inputCls} placeholder="örn. 7" /></div>
                    <div className="sm:col-span-2"><label className={labelCls}>Açıklama</label>
                      <textarea data-testid={`quote-offer-desc-${r.id}`} rows={2} value={offer.description}
                        onChange={(e) => setOffer({ ...offer, description: e.target.value })} className={inputCls} placeholder="Teklif detayı..." /></div>
                    <div className="sm:col-span-2 flex gap-2">
                      <button data-testid={`quote-offer-submit-${r.id}`} disabled={sending} onClick={() => submitOffer(r.id)}
                        className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-emerald-500 text-slate-950 text-sm font-bold hover:bg-emerald-400 transition-colors disabled:opacity-50">
                        <Send className="w-4 h-4" /> {sending ? "Gönderiliyor..." : r.my_offer ? "Teklifi Güncelle" : "Teklifi Gönder"}
                      </button>
                      <button onClick={() => setOpenForm(null)}
                        className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-400 text-sm hover:text-slate-200 transition-colors">Vazgeç</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {dossierFor && <VehicleDossierModal request={dossierFor} onClose={() => setDossierFor(null)} />}
    </div>
  );
}
